import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import CommonModal from './config/CommonModal';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { handleInterComClick } from './config/FunctionHelper';
import CloseIcon from '@mui/icons-material/Close';

export interface CommonAlertModalProps {
  status: 'success' | 'error' | '' | null | undefined;
  message: React.ReactNode;
  onClose?: () => void;
}

export const CommonAlertModal: React.FC<CommonAlertModalProps> = ({
  status,
  message,
  onClose
}) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (status && message) {
      setIsOpen(true);
      const timer = setTimeout(() => {
        setIsOpen(false);
        if (onClose) onClose();
      }, 5000); // Auto-close after 5 seconds
      return () => clearTimeout(timer);
    } else {
      setIsOpen(false);
    }
  }, [status, message, onClose]);

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  const isSuccess = status === 'success';

  // Theme-aligned
  const titleText = isSuccess ? 'Payment Successful!' : 'Payment Unsuccessful';

  return (
    <CommonModal
      open={isOpen}
      handCloseModal={handleClose}
      title=""
      closebtn={false}
      isAlertModal={true}
      isDynamicWidth={600}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '600px',
          margin: '0 auto',
          background: '#FFFFFF',
          borderRadius: '12px',
          boxShadow:
            '0 20px 60px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.06)',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <IconButton
          aria-label="close"
          className="hover:rounded-lg"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 2,
            height: '40px',
            padding: '8px',
            paddingRight: '8px'
          }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>

        {/* Top gradient band */}
        <Box
          sx={{
            background: "white",
            padding: '36px 24px 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative'
          }}
        >
          {isSuccess ? (
            /* Icon circle for success */
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                backgroundColor: '#120242',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(18, 2, 66, 0.3)',
                position: 'relative',
                zIndex: 1
              }}
            >
              <CheckCircleOutlineRoundedIcon
                sx={{ fontSize: 38, color: '#FFFFFF' }}
              />
            </Box>
          ) : (
            /* Illustration image for error */
            <img
              src={
                process.env.PUBLIC_URL +
                '/static/images/icons/paymentUnSuccessfull.png'
              }
              alt="Payment Unsuccessful"
              style={{
                width: '180px',
                height: 'auto',
                position: 'relative',
                zIndex: 1
              }}
            />
          )}

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              color: isSuccess ? '#120242' : '#F45252',
              marginTop: '18px',
              textAlign: 'center'
            }}
          >
            {titleText}
          </Typography>
        </Box>

        {/* Content area */}
        <Box
          sx={{
            padding: '20px 32px 32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          {/* Message */}
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontSize: 'var(--font-size-15)',
              fontWeight: 500,
              lineHeight: 'var(--line-height-24)',
              color: '#5D5F6C',
              textAlign: 'justify',
              maxWidth: '580px'
            }}
          >
            {message}
          </Typography>

          {/* Action buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: '12px',
              marginTop: '24px',
              width: '100%',
              justifyContent: 'center',
              flexDirection: isSuccess ? 'column' : 'row',
              flexWrap: 'wrap'
            }}
          >
            {isSuccess ? (
              <Button
                variant="contained"
                onClick={handleClose}
                disableElevation
                sx={{
                  flex: 1,
                  padding: '10px 18px',
                  borderRadius: '6px',
                  backgroundColor: '#120242',
                  color: '#FFFFFF',
                  fontFamily: 'Inter',
                  fontSize: 'var(--font-size-14)',
                  fontWeight: 600,
                  lineHeight: 'var(--line-height-20)',
                  textTransform: 'none',
                  minWidth: '160px',
                  transition: 'all 0.25s ease',
                  boxShadow: '0px 1px 2px 0px #1018280D',
                  '&:hover': {
                    backgroundColor: '#1E0359',
                    boxShadow: '0px 4px 12px rgba(18, 2, 66, 0.3)'
                  },
                  '&:active': {
                    transform: 'translateY(0)'
                  }
                }}
              >
                Continue
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={handleInterComClick}
                  sx={{
                    flex: 1,
                    padding: '10px 18px',
                    borderRadius: '6px',
                    borderColor: '#120242',
                    color: '#120242',
                    fontFamily: 'Inter',
                    fontSize: 'var(--font-size-14)',
                    fontWeight: 600,
                    lineHeight: 'var(--line-height-20)',
                    textTransform: 'none',
                    minWidth: '140px',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      borderColor: '#1E0359',
                      backgroundColor: 'rgba(18, 2, 66, 0.04)',
                    },
                    '&:active': {
                      transform: 'translateY(0)'
                    }
                  }}
                >
                  Contact Support
                </Button>
                <Button
                  variant="contained"
                  onClick={handleClose}
                  disableElevation
                  sx={{
                    flex: 1,
                    padding: '10px 18px',
                    borderRadius: '6px',
                    backgroundColor: '#120242',
                    color: '#FFFFFF',
                    fontFamily: 'Inter',
                    fontSize: 'var(--font-size-14)',
                    fontWeight: 600,
                    lineHeight: 'var(--line-height-20)',
                    textTransform: 'none',
                    minWidth: '140px',
                    transition: 'all 0.25s ease',
                    boxShadow: '0px 1px 2px 0px #1018280D',
                    '&:hover': {
                      backgroundColor: '#1E0359',
                      boxShadow: '0px 4px 12px rgba(18, 2, 66, 0.3)'
                    },
                    '&:active': {
                      transform: 'translateY(0)'
                    }
                  }}
                >
                  Try again
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </CommonModal>
  );
};
